import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowsClockwise, Boat, Buildings, Camera, Check, ClipboardText, Clock,
    Gauge, Package, Pause, Play, Truck, User, WarningOctagon, ChartLineUp, Scales,
} from "@phosphor-icons/react";
import TruckArrivalModal from "./TruckArrivalModal";
import WeighOutModal from "./WeighOutModal";
import LogSiloTonsModal from "./LogSiloTonsModal";
import PauseExportModal from "./PauseExportModal";
import ChangeSiloModal from "./ChangeSiloModal";
import TruckPickerModal from "./TruckPickerModal";

const NUM = (n, d = 2) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: d });

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
    catch { return iso; }
}

function StatusStepper({ step }) {
    // step: 0=started, 1=loading, 2=export-in-progress, 3=complete
    const steps = [
        { label: "Export started", icon: Play },
        { label: "Loading in progress", icon: Truck },
        { label: "Export in progress", icon: Boat },
        { label: "Export complete", icon: Check },
    ];
    return (
        <div className="flex items-center gap-1 flex-wrap" data-testid="load-out-stepper">
            {steps.map((s, i) => {
                const active = i <= step;
                const current = i === step;
                const Icon = s.icon;
                return (
                    <React.Fragment key={s.label}>
                        <div className="flex items-center gap-1.5">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${active ? "border-emerald-400 bg-emerald-500/20 text-emerald-300" : "border-slate-600 text-slate-500"}`}>
                                <Icon size={14} weight="bold" />
                            </div>
                            <div className={`text-[10px] uppercase tracking-wider font-bold ${current ? "text-emerald-300" : active ? "text-slate-300" : "text-slate-500"}`}>
                                {s.label}
                            </div>
                        </div>
                        {i < steps.length - 1 && <div className={`h-0.5 w-4 sm:w-6 ${active ? "bg-emerald-500/60" : "bg-slate-700"}`} />}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

/** Simple SVG gauge for flow rate (t/h) */
function FlowGauge({ value = 0 }) {
    const v = Number(value) || 0;
    // Auto-scale max in 500 t/h steps so the label never lies when flow > 1200.
    const max = Math.max(1200, Math.ceil(v / 500) * 500);
    const clamped = Math.max(0, Math.min(max, v));
    const angle = (clamped / max) * 180 - 90; // -90 (left) to +90 (right)
    const color = v < max * 0.25 ? "#EF4444" : v < max * 0.5 ? "#F59E0B" : v < max * 0.8 ? "#EAB308" : "#22C55E";
    return (
        <svg viewBox="0 0 200 120" className="w-full max-w-[220px]" role="img" aria-label="Export average flow rate">
            <defs>
                <linearGradient id="gaugeGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#EF4444" />
                    <stop offset="35%" stopColor="#F59E0B" />
                    <stop offset="70%" stopColor="#EAB308" />
                    <stop offset="100%" stopColor="#22C55E" />
                </linearGradient>
            </defs>
            <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#gaugeGrad)" strokeWidth="16" strokeLinecap="round" />
            {/* Needle */}
            <g transform={`translate(100 100) rotate(${angle})`}>
                <line x1="0" y1="0" x2="0" y2="-70" stroke="#F1F5F9" strokeWidth="3" strokeLinecap="round" />
                <circle cx="0" cy="0" r="6" fill="#F1F5F9" stroke={color} strokeWidth="2" />
            </g>
            <text x="20" y="118" fontSize="10" fill="#94A3B8">0</text>
            <text x="170" y="118" fontSize="10" fill="#94A3B8">{max}</text>
            <text x="100" y="80" fontSize="24" fontWeight="900" fill={color} textAnchor="middle" data-testid="flow-gauge-value">{Math.round(v)}</text>
            <text x="100" y="95" fontSize="9" fill="#94A3B8" textAnchor="middle">t/h</text>
        </svg>
    );
}

/** Practical technical illustration: bulk cargo truck + skip bin being loaded from silo chute. */
function LoadOutIllustration({ siloNo, product, active = true }) {
    // Simple pseudo-random animation dots for the falling product stream
    const streamDots = [0, 1, 2, 3, 4, 5];
    return (
        <div className="relative rounded-lg h-72 overflow-hidden border border-slate-700 bg-gradient-to-b from-sky-100 via-sky-50 to-slate-200" data-testid="load-out-illustration">
            <svg viewBox="0 0 400 320" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="siloBody" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#CBD5E1" />
                        <stop offset="45%" stopColor="#F1F5F9" />
                        <stop offset="55%" stopColor="#F1F5F9" />
                        <stop offset="100%" stopColor="#94A3B8" />
                    </linearGradient>
                    <linearGradient id="skipBody" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F59E0B" />
                        <stop offset="70%" stopColor="#D97706" />
                        <stop offset="100%" stopColor="#B45309" />
                    </linearGradient>
                    <linearGradient id="cabBody" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E2E8F0" />
                        <stop offset="50%" stopColor="#94A3B8" />
                        <stop offset="100%" stopColor="#475569" />
                    </linearGradient>
                    <linearGradient id="chassis" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#334155" />
                        <stop offset="100%" stopColor="#0F172A" />
                    </linearGradient>
                </defs>

                {/* Ground shadow */}
                <ellipse cx="200" cy="288" rx="150" ry="6" fill="#000" opacity="0.15" />

                {/* ─── SILO ────────────────────────────────────────────── */}
                {/* Body */}
                <rect x="140" y="10" width="120" height="120" rx="4" fill="url(#siloBody)" stroke="#64748B" strokeWidth="1.2" />
                {/* Corrugation ribs */}
                {[20, 40, 60, 80, 100, 120].map((y) => (
                    <line key={y} x1="140" y1={y} x2="260" y2={y} stroke="#94A3B8" strokeWidth="0.6" opacity="0.5" />
                ))}
                {/* Top ring / cap */}
                <ellipse cx="200" cy="10" rx="60" ry="6" fill="#94A3B8" stroke="#475569" strokeWidth="1" />
                <rect x="188" y="0" width="24" height="8" rx="1" fill="#475569" />
                {/* Silo label */}
                <rect x="164" y="55" width="72" height="26" rx="3" fill="#0F172A" opacity="0.85" />
                <text x="200" y="70" fontSize="14" fontWeight="900" fill="#38BDF8" textAnchor="middle" letterSpacing="2">SILO {siloNo}</text>
                <text x="200" y="80" fontSize="7" fontWeight="700" fill="#94A3B8" textAnchor="middle" letterSpacing="1">{(product || "").toString().toUpperCase()}</text>
                {/* Ladder */}
                <line x1="246" y1="20" x2="246" y2="125" stroke="#475569" strokeWidth="1.4" />
                {[35, 50, 65, 80, 95, 110].map((y) => (
                    <line key={y} x1="242" y1={y} x2="250" y2={y} stroke="#475569" strokeWidth="1.2" />
                ))}
                {/* Discharge cone */}
                <polygon points="140,130 260,130 220,175 180,175" fill="url(#siloBody)" stroke="#64748B" strokeWidth="1.2" />
                {/* Chute (rectangular pipe) */}
                <rect x="188" y="173" width="24" height="26" fill="#0F766E" stroke="#134E4A" strokeWidth="1" />
                {/* Chute lip */}
                <rect x="184" y="197" width="32" height="4" rx="1" fill="#0F172A" />

                {/* ─── PRODUCT STREAM (animated) ─────────────────────── */}
                {active && streamDots.map((i) => (
                    <g key={i}>
                        <rect x={195 + (i % 2) * 4} y={205 + i * 4} width="4" height="4" fill="#F59E0B" opacity={0.9 - i * 0.05}>
                            <animate attributeName="y" from="200" to="235" dur="0.6s" begin={`${i * 0.08}s`} repeatCount="indefinite" />
                            <animate attributeName="opacity" from="1" to="0" dur="0.6s" begin={`${i * 0.08}s`} repeatCount="indefinite" />
                        </rect>
                    </g>
                ))}
                {/* Small pile on top of skip */}
                <ellipse cx="200" cy="230" rx="26" ry="4" fill="#F59E0B" opacity="0.9" />
                <polygon points="180,230 220,230 200,222" fill="#D97706" />

                {/* ─── TRUCK ────────────────────────────────────────── */}
                {/* Skip bin (dumpster) on trailer */}
                <g>
                    {/* Bin body */}
                    <polygon points="140,232 260,232 275,278 125,278" fill="url(#skipBody)" stroke="#78350F" strokeWidth="1.5" />
                    {/* Ribs on skip */}
                    {[160, 180, 200, 220, 240].map((x) => (
                        <line key={x} x1={x} y1="234" x2={x + 5} y2="276" stroke="#78350F" strokeWidth="0.9" opacity="0.5" />
                    ))}
                    {/* Skip top opening */}
                    <rect x="140" y="230" width="120" height="4" fill="#78350F" opacity="0.6" />
                    {/* Hinge / lift point */}
                    <circle cx="140" cy="240" r="3" fill="#0F172A" />
                    <circle cx="260" cy="240" r="3" fill="#0F172A" />
                </g>
                {/* Truck chassis under skip */}
                <rect x="105" y="278" width="180" height="6" fill="url(#chassis)" />
                {/* Cab in front */}
                <g>
                    <rect x="60" y="238" width="50" height="46" rx="4" fill="url(#cabBody)" stroke="#0F172A" strokeWidth="1.2" />
                    {/* Windshield */}
                    <polygon points="66,244 104,244 100,258 70,258" fill="#7DD3FC" stroke="#0F172A" strokeWidth="0.8" />
                    {/* Door line */}
                    <line x1="85" y1="260" x2="85" y2="284" stroke="#0F172A" strokeWidth="0.8" />
                    {/* Headlight */}
                    <rect x="56" y="266" width="6" height="6" rx="1" fill="#FCD34D" />
                    {/* Bumper */}
                    <rect x="52" y="278" width="60" height="6" fill="#334155" />
                    {/* Grille */}
                    <rect x="58" y="270" width="4" height="8" rx="1" fill="#0F172A" />
                </g>
                {/* Fuel tank between cab and trailer */}
                <rect x="112" y="270" width="16" height="10" rx="2" fill="#64748B" stroke="#0F172A" strokeWidth="0.6" />

                {/* Wheels */}
                {[80, 132, 210, 250].map((cx) => (
                    <g key={cx}>
                        <circle cx={cx} cy="288" r="9" fill="#0F172A" stroke="#334155" strokeWidth="1" />
                        <circle cx={cx} cy="288" r="4" fill="#475569" />
                        <line x1={cx - 3} y1="288" x2={cx + 3} y2="288" stroke="#0F172A" strokeWidth="0.6" />
                        <line x1={cx} y1="285" x2={cx} y2="291" stroke="#0F172A" strokeWidth="0.6" />
                    </g>
                ))}

                {/* OPERATIONAL glow */}
                {active && (
                    <g>
                        <circle cx="380" cy="20" r="5" fill="#22C55E">
                            <animate attributeName="opacity" from="0.4" to="1" dur="1.2s" repeatCount="indefinite" />
                        </circle>
                    </g>
                )}
            </svg>
        </div>
    );
}

/**
 * LoadOutStation — Active export operation page. Matches the "LOAD OUT STATION 1" mock.
 */
export default function LoadOutStation({ exportId, onBack }) {
    const { user } = useAuth();
    const [exp, setExp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // 'arrival' | 'weigh' | 'silo-tons' | 'pause' | 'change'
    const [weighVisit, setWeighVisit] = useState(null);
    const [now, setNow] = useState(new Date());
    const pollRef = useRef(null);

    const canOperate = ["admin", "supervisor"].includes(user?.role);

    const load = async () => {
        try {
            const r = await api.get(`/tronox/exports/${exportId}`);
            setExp(r.data);
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };

    useEffect(() => {
        load();
        pollRef.current = setInterval(load, 8000);
        const clockId = setInterval(() => setNow(new Date()), 1000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            clearInterval(clockId);
        };
    }, [exportId]);

    const resumeExport = async () => {
        try {
            await api.post(`/tronox/exports/${exportId}/resume`, {});
            toast.success("Operation resumed.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const completeExport = async () => {
        if (!window.confirm(`Complete this export operation? This will lock the vessel record and no further tons or trucks can be logged.`)) return;
        try {
            await api.post(`/tronox/exports/${exportId}/complete`, {});
            toast.success("Export completed.");
            onBack();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading || !exp) {
        return (
            <div className="rounded-xl bg-slate-950 text-slate-100 p-10 text-center" data-testid="load-out-station-loading">Loading load-out station…</div>
        );
    }

    const m = exp.metrics || {};
    const visits = exp.truck_visits || [];
    const atStation = visits.filter((v) => v.status === "at_station");
    const pendingWeighOut = visits.filter((v) => v.status === "pending_weigh_out");
    const completedVisits = visits.filter((v) => v.status === "weighed_out");
    const latestVisit = visits[visits.length - 1];
    const isPaused = !!m.is_paused;
    const step = exp.status === "completed" ? 3 : visits.length > 0 ? 2 : 1;

    const sendToWeighbridge = async (visitId) => {
        try {
            await api.post(`/tronox/exports/${exportId}/trucks/${visitId}/send-to-weighbridge`, {});
            toast.success("Truck sent to weighbridge — awaiting weigh-out.");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="rounded-xl bg-slate-950 text-slate-100 shadow-inner pb-24 xl:pb-0" data-testid="load-out-station">
            {/* Top banner */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 bg-slate-900/60 rounded-t-xl">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="p-2 rounded hover:bg-slate-800 text-slate-300" data-testid="load-out-back">
                        <ArrowLeft size={18} weight="bold" />
                    </button>
                    <div>
                        <div className="text-[11px] uppercase tracking-widest text-slate-400">GT Vision · Grindrod Terminals</div>
                        <div className="text-2xl font-black tracking-tight text-emerald-300">LOAD OUT STATION 1</div>
                    </div>
                </div>
                <div className="text-right text-xs text-slate-400 font-mono">
                    <div>{now.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</div>
                    <div className="text-emerald-400">{now.toLocaleTimeString()}</div>
                </div>
            </div>

            {/* PAUSED BANNER — impossible to miss */}
            {isPaused && exp.status === "active" && (
                <div className="mx-3 sm:mx-4 mt-3 rounded-lg border-2 border-rose-500 bg-rose-950/70 p-3 flex flex-wrap items-center justify-between gap-2 animate-pulse" data-testid="paused-banner">
                    <div className="flex items-center gap-2">
                        <Pause size={22} weight="fill" className="text-rose-300" />
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-rose-300 font-bold">Operation paused</div>
                            <div className="text-sm font-black text-white">
                                {(exp.pauses || []).filter((p) => p.ended_at === null).slice(-1)[0]?.reason || "—"}
                            </div>
                        </div>
                    </div>
                    {canOperate && (
                        <button
                            onClick={resumeExport}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-black"
                            data-testid="resume-operation-btn"
                        >
                            <Play size={14} weight="fill" /> Resume
                        </button>
                    )}
                </div>
            )}

            {/* PRIMARY ACTION BAR — mirrors the physical workflow, top → bottom:
                1. TRUCK IN (arrive at station)
                2. TRUCK OUT (confirm finished loading → sent to weighbridge)
                3. WEIGH OUT (capture weighbridge tons)
                4. LOG SILO TONS (conveyor scale reading)
                5. PAUSE / RESUME (breakdowns) */}
            {canOperate && exp.status === "active" && (
                <div className="px-3 sm:px-4 pt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2" data-testid="lo-action-bar">
                    <button
                        onClick={() => setModal("arrival")}
                        disabled={isPaused}
                        className="inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black shadow-lg ring-1 ring-emerald-300/40"
                        data-testid="lo-truck-in-btn"
                    >
                        <Truck size={20} weight="fill" />
                        <span className="text-sm">+ TRUCK IN</span>
                    </button>
                    <button
                        onClick={() => {
                            if (isPaused) return;
                            if (atStation.length === 0) { toast.info("No trucks at station to send to weighbridge."); return; }
                            if (atStation.length === 1) { sendToWeighbridge(atStation[0].id); return; }
                            setModal("pick-out");
                        }}
                        disabled={isPaused || atStation.length === 0}
                        className="relative inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black shadow-lg ring-1 ring-amber-300/40"
                        data-testid="lo-truck-out-btn"
                        title="Truck finished loading — send to weighbridge"
                    >
                        <Truck size={20} weight="regular" />
                        <span className="text-sm">TRUCK OUT</span>
                        {atStation.length > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-white text-amber-700 text-[11px] font-black flex items-center justify-center border-2 border-amber-500" data-testid="lo-truck-out-badge">{atStation.length}</span>
                        )}
                    </button>
                    <button
                        onClick={() => {
                            if (isPaused) return;
                            // Weigh-out primarily targets pending trucks; fall back to at-station for shortcuts.
                            const pool = pendingWeighOut.length > 0 ? pendingWeighOut : atStation;
                            if (pool.length === 0) { toast.info("No trucks awaiting weigh-out."); return; }
                            if (pool.length === 1) { setWeighVisit(pool[0]); setModal("weigh"); return; }
                            setModal("pick-weigh");
                        }}
                        disabled={isPaused || (pendingWeighOut.length === 0 && atStation.length === 0)}
                        className="relative inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-400 hover:to-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black shadow-lg ring-1 ring-sky-300/40"
                        data-testid="lo-weigh-out-btn"
                    >
                        <Scales size={20} weight="fill" />
                        <span className="text-sm">WEIGH OUT</span>
                        {pendingWeighOut.length > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-white text-sky-700 text-[11px] font-black flex items-center justify-center border-2 border-sky-500" data-testid="lo-weigh-out-badge">{pendingWeighOut.length}</span>
                        )}
                    </button>
                    <button
                        onClick={() => setModal("silo-tons")}
                        disabled={isPaused}
                        className="inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 disabled:opacity-40 disabled:cursor-not-allowed text-white font-black shadow-lg ring-1 ring-amber-300/40"
                        data-testid="lo-log-silo-tons-btn"
                    >
                        <ChartLineUp size={20} weight="fill" />
                        <span className="text-sm">LOG SILO TONS</span>
                    </button>
                    {isPaused ? (
                        <button
                            onClick={resumeExport}
                            className="inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black shadow-lg ring-1 ring-emerald-300/40"
                            data-testid="lo-resume-btn"
                        >
                            <Play size={20} weight="fill" />
                            <span className="text-sm">RESUME</span>
                        </button>
                    ) : (
                        <button
                            onClick={() => setModal("pause")}
                            className="inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white font-black shadow-lg ring-1 ring-rose-300/40"
                            data-testid="lo-pause-btn"
                        >
                            <Pause size={20} weight="fill" />
                            <span className="text-sm">PAUSE</span>
                        </button>
                    )}
                </div>
            )}

            {/* ASSIGNED TRANSPORTERS — auto-populates from plate scans + manual add */}
            {exp.status === "active" && (
                <div className="px-3 sm:px-4 pt-3" data-testid="lo-assigned-transporters">
                    <div className="text-[10px] uppercase tracking-widest text-slate-300 font-bold mb-1.5 flex items-center gap-1">
                        <Buildings size={11} weight="fill" /> Assigned transporters ({(exp.assigned_transporters || []).length})
                    </div>
                    {(exp.assigned_transporters || []).length === 0 ? (
                        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2 text-center text-[11px] text-slate-500">
                            No transporters assigned yet. They&apos;re auto-added when a registered plate is scanned.
                        </div>
                    ) : (
                        <div className="flex flex-wrap gap-1.5">
                            {(exp.assigned_transporters || []).map((tp) => (
                                <div key={tp.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-800/80 border border-slate-700 text-xs text-slate-100" data-testid={`assigned-transporter-${tp.id}`}>
                                    <Buildings size={12} className="text-emerald-300" />
                                    <span className="font-bold">{tp.company_name}</span>
                                    {tp.emergency_contact_phone && <span className="text-slate-400 text-[10px]">· {tp.emergency_contact_phone}</span>}
                                    {tp.auto_added && <span className="text-[9px] text-emerald-400 uppercase tracking-wider">auto</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ACTIVE TRUCKS — two panels for multi-user parallel operation.
                Left/top: "AT STATION" (load-out supervisor's view — they load skips then hand off).
                Right/bottom: "PENDING WEIGH-OUT" (weighbridge supervisor's view — capture tons). */}
            {canOperate && exp.status === "active" && (atStation.length > 0 || pendingWeighOut.length > 0) && (
                <div className="px-3 sm:px-4 pt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="lo-active-trucks">
                    {/* AT STATION */}
                    <div className={atStation.length === 0 ? "hidden md:block" : ""} data-testid="lo-at-station-panel">
                        <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1.5 flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                            Loading at station ({atStation.length})
                        </div>
                        {atStation.length === 0 ? (
                            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-center text-[11px] text-slate-500">
                                No trucks currently loading. Tap <b className="text-emerald-300">+ TRUCK IN</b> when the next truck arrives.
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {atStation.map((v) => {
                                    const arrivedMs = new Date(v.arrived_at).getTime();
                                    const elapsedMin = Math.max(0, Math.floor((now.getTime() - arrivedMs) / 60000));
                                    return (
                                        <div key={v.id} className="rounded-lg border border-amber-500/40 bg-slate-900/60 p-2 flex items-center justify-between gap-2" data-testid={`active-truck-${v.id}`}>
                                            <div className="flex items-center gap-2">
                                                <Truck size={22} className="text-amber-300" />
                                                <div>
                                                    <div className="font-mono text-base font-black text-white leading-tight">{v.plate}</div>
                                                    <div className="text-[10px] text-slate-400">
                                                        {v.skips} skip{v.skips === 1 ? "" : "s"} · Silo {v.silo_no} · <span className="text-amber-300">{elapsedMin}m loading</span>
                                                    </div>
                                                    {v.transporter?.company_name && (
                                                        <div className="text-[10px] text-emerald-300 font-bold truncate max-w-[220px]" data-testid={`active-truck-transporter-${v.id}`}>{v.transporter.company_name}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => sendToWeighbridge(v.id)}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white text-xs font-black whitespace-nowrap"
                                                data-testid={`send-to-weighbridge-btn-${v.id}`}
                                                title="Truck has finished loading — send to weighbridge"
                                            >
                                                → Weighbridge
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* PENDING WEIGH-OUT */}
                    <div className={pendingWeighOut.length === 0 ? "hidden md:block" : ""} data-testid="lo-pending-weighout-panel">
                        <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold mb-1.5 flex items-center gap-1">
                            <Scales size={11} weight="fill" />
                            Pending weigh-out ({pendingWeighOut.length})
                        </div>
                        {pendingWeighOut.length === 0 ? (
                            <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-center text-[11px] text-slate-500">
                                No trucks waiting at the weighbridge.
                            </div>
                        ) : (
                            <div className="space-y-1.5">
                                {pendingWeighOut.map((v) => {
                                    const departedMs = new Date(v.departed_at || v.arrived_at).getTime();
                                    const elapsedMin = Math.max(0, Math.floor((now.getTime() - departedMs) / 60000));
                                    return (
                                        <div key={v.id} className="rounded-lg border border-sky-500/50 bg-sky-950/40 p-2 flex items-center justify-between gap-2" data-testid={`pending-truck-${v.id}`}>
                                            <div className="flex items-center gap-2">
                                                <Scales size={22} className="text-sky-300" weight="fill" />
                                                <div>
                                                    <div className="font-mono text-base font-black text-white leading-tight">{v.plate}</div>
                                                    <div className="text-[10px] text-slate-400">
                                                        {v.skips} skip{v.skips === 1 ? "" : "s"} · Silo {v.silo_no} · <span className="text-sky-300">{elapsedMin}m awaiting</span>
                                                    </div>
                                                    {v.transporter?.company_name && (
                                                        <div className="text-[10px] text-sky-300 font-bold truncate max-w-[220px]">{v.transporter.company_name}</div>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => { setWeighVisit(v); setModal("weigh"); }}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-500 text-white text-sm font-black whitespace-nowrap"
                                                data-testid={`weigh-out-btn-${v.id}`}
                                            >
                                                <Scales size={14} weight="bold" /> Weigh Out
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="p-4 grid grid-cols-1 xl:grid-cols-12 gap-4">
                {/* Left column — illustration + current loading */}
                <div className="xl:col-span-5 space-y-3">
                    <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 text-[10px] font-bold uppercase tracking-wider">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Operational
                            </span>
                            <div className="text-xs text-slate-400">
                                <span className="uppercase text-[10px] tracking-wider">Product source</span>
                                <span className="ml-2 font-black text-teal-300 text-sm">SILO {exp.current_silo_no}</span>
                            </div>
                        </div>
                        <LoadOutIllustration siloNo={exp.current_silo_no} product={exp.product} />
                        {canOperate && exp.status === "active" && (
                            <button
                                onClick={() => setModal("change")}
                                className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold"
                                data-testid="load-out-change-silo"
                            >
                                <ArrowsClockwise size={14} weight="bold" /> Change source silo
                            </button>
                        )}
                    </div>
                </div>

                {/* Middle column — current loading details */}
                <div className="xl:col-span-4 space-y-2">
                    <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3">
                        <div className="text-[11px] uppercase tracking-widest text-sky-300 font-bold mb-2 flex items-center gap-1"><Truck size={14} /> Current loading</div>
                        <InfoRow label="Truck registration" value={latestVisit?.plate || "— awaiting first truck —"} icon={Truck} testId="lo-latest-plate" />
                        <InfoRow label="Loading start time" value={fmt(exp.started_at)} icon={Clock} testId="lo-start-time" />
                        <InfoRow label="Product" value={exp.product} icon={Package} testId="lo-product" />
                        <InfoRow label="Loading from" value={`SILO ${exp.current_silo_no}`} icon={Buildings} testId="lo-current-silo" />
                        <InfoRow label="Current skip count" value={NUM(m.total_skips, 0)} icon={ClipboardText} testId="lo-current-skips" />
                        <InfoRow label="Operation status" value={exp.status === "active" ? "LOADING" : "COMPLETED"} status={exp.status} testId="lo-op-status" />
                    </div>
                </div>

                {/* Right column — big totals + gauge + action */}
                <div className="xl:col-span-3 space-y-3">
                    <div className="rounded-xl bg-slate-900/60 border border-emerald-500/30 p-3 text-center" data-testid="lo-skip-count-card">
                        <div className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold">Loaded skip count</div>
                        <div className="text-5xl font-black text-emerald-300 mt-1" data-testid="lo-skip-count-value">{NUM(m.total_skips, 0)}</div>
                        <div className="text-[10px] uppercase text-slate-400 tracking-wider">skips loaded</div>
                    </div>
                    <div className="rounded-xl bg-slate-900/60 border border-sky-500/30 p-3">
                        <div className="text-[11px] uppercase tracking-widest text-sky-300 font-bold mb-1 flex items-center gap-1"><Gauge size={14} /> Flow rate <span className="text-[9px] text-slate-500 font-normal">(net · excl. pauses)</span></div>
                        <div className="flex justify-center">
                            <FlowGauge value={m.flow_rate_tph || 0} />
                        </div>
                    </div>
                    <div className="rounded-xl bg-slate-900/60 border border-purple-500/30 p-2 text-center" data-testid="lo-overall-performance-card">
                        <div className="text-[10px] uppercase tracking-widest text-purple-300 font-bold">Overall performance</div>
                        <div className="text-2xl font-black text-purple-300 mt-0.5" data-testid="lo-overall-performance-value">{NUM(m.overall_performance_tph, 1)}</div>
                        <div className="text-[9px] uppercase text-slate-500 tracking-wider">t/h · incl. delays</div>
                    </div>
                    {canOperate && exp.status === "active" && (
                        <button
                            onClick={() => setModal("silo-tons")}
                            disabled={isPaused}
                            className="w-full py-3 rounded-xl bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-black flex flex-col items-center gap-1 shadow-lg"
                            data-testid="lo-log-silo-tons-side"
                        >
                            <ChartLineUp size={22} weight="fill" />
                            LOG SILO TONS
                            <span className="text-[10px] font-normal opacity-80">Conveyor scale reading</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Bottom action bar — totals (weighbridge primary, conveyor cross-check) + complete */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-4 pt-0">
                <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 flex items-center gap-2">
                    <Scales size={26} className="text-sky-300" />
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">Weighbridge total</div>
                        <div className="text-xl font-black text-sky-300" data-testid="lo-total-tons">{NUM(m.truck_total_tons)}</div>
                        <div className="text-[10px] text-slate-500">tons · drives flow rate</div>
                    </div>
                </div>
                <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 flex items-center gap-2">
                    <ChartLineUp size={26} className="text-amber-300" />
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-400">Conveyor total</div>
                        <div className="text-xl font-black text-amber-300" data-testid="lo-conveyor-tons">{NUM(m.conveyor_total_tons)}</div>
                        <div className="text-[10px] text-slate-500">tons · silo scale cross-check</div>
                    </div>
                </div>
                {canOperate && exp.status === "active" ? (
                    <>
                        <button
                            onClick={completeExport}
                            className="rounded-xl bg-emerald-800/40 hover:bg-emerald-700/60 border border-emerald-600 text-emerald-100 p-3 flex items-center justify-center gap-2 font-black uppercase tracking-wider"
                            data-testid="lo-complete-export"
                        >
                            <Check size={22} weight="bold" /> Complete export
                        </button>
                        <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3 text-center text-[10px] text-slate-500 flex items-center justify-center">
                            Paused: <b className="text-rose-300 ml-1">{NUM(m.paused_minutes, 1)} min</b><br />
                            Net running: <b className="text-emerald-300 ml-1">{NUM(m.running_hours, 2)} h</b>
                        </div>
                    </>
                ) : (
                    <div className="md:col-span-2 rounded-xl bg-slate-900/60 border border-slate-800 p-3 text-center text-xs text-slate-400">
                        Export completed on {fmt(exp.completed_at)} by {exp.completed_by_name || "—"}.
                    </div>
                )}
            </div>

            {/* Vessel + system status footer */}
            <div className="border-t border-slate-800 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3">
                    <div className="text-[11px] uppercase tracking-widest text-sky-300 font-bold mb-2">Overall Export Information</div>
                    <div className="grid grid-cols-2 gap-2">
                        <InfoRow label="Export start" value={fmt(exp.started_at)} icon={Clock} testId="lo-info-start" />
                        <InfoRow label="Vessel name" value={exp.vessel_name} icon={Boat} testId="lo-info-vessel" />
                        <InfoRow label="Client name" value={exp.client_name} icon={User} testId="lo-info-client" />
                        <InfoRow label="Division" value="GTRB" icon={Buildings} testId="lo-info-division" />
                    </div>
                </div>
                <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3">
                    <div className="text-[11px] uppercase tracking-widest text-sky-300 font-bold mb-2">System Status</div>
                    <StatusStepper step={step} />
                    <div className="mt-3 text-[11px] text-emerald-300 font-bold" data-testid="lo-status-message">
                        {exp.status === "active"
                            ? `LOADING IN PROGRESS FROM SILO ${exp.current_silo_no} — PRODUCT: ${exp.product}`
                            : "EXPORT COMPLETE"}
                    </div>
                </div>
            </div>

            {/* Fleet + segments summary */}
            {(m.trucks?.length > 0 || (m.silo_segments || []).length > 0) && (
                <div className="border-t border-slate-800 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3" data-testid="lo-fleet-panel">
                        <div className="text-[11px] uppercase tracking-widest text-slate-300 font-bold mb-2">Fleet performance</div>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                            <Stat label="Unique trucks" value={NUM(m.unique_trucks, 0)} testId="lo-unique-trucks" />
                            <Stat label="Total collections" value={NUM(m.total_collections, 0)} testId="lo-total-collections" />
                            <Stat label="Avg turnaround" value={m.fleet_avg_turnaround_minutes != null ? `${NUM(m.fleet_avg_turnaround_minutes, 1)} min` : "—"} testId="lo-fleet-turnaround" />
                        </div>
                        <div className="max-h-56 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="text-slate-400 text-[10px] uppercase tracking-wider">
                                    <tr><th className="text-left p-1">Plate</th><th className="text-right p-1">Loads</th><th className="text-right p-1">Skips</th><th className="text-right p-1">Tons</th><th className="text-right p-1">Avg gap</th></tr>
                                </thead>
                                <tbody>
                                    {(m.trucks || []).map((t) => (
                                        <tr key={t.plate} className="border-t border-slate-800/50" data-testid={`lo-truck-row-${t.plate}`}>
                                            <td className="p-1 font-mono">{t.plate}</td>
                                            <td className="p-1 text-right">{t.loads}</td>
                                            <td className="p-1 text-right">{t.skips}</td>
                                            <td className="p-1 text-right">{NUM(t.tons)}</td>
                                            <td className="p-1 text-right">{t.avg_turnaround_minutes != null ? `${t.avg_turnaround_minutes} m` : "—"}</td>
                                        </tr>
                                    ))}
                                    {(m.trucks || []).length === 0 && (
                                        <tr><td colSpan={5} className="text-center text-slate-500 py-3">No trucks logged yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="rounded-xl bg-slate-900/60 border border-slate-800 p-3" data-testid="lo-segments-panel">
                        <div className="text-[11px] uppercase tracking-widest text-slate-300 font-bold mb-2">Silo segments</div>
                        <div className="max-h-64 overflow-y-auto space-y-1">
                            {(m.silo_segments || []).map((s, i) => (
                                <div key={i} className="flex items-center justify-between text-xs bg-slate-800/60 rounded px-2 py-1" data-testid={`lo-segment-${i}`}>
                                    <div>
                                        <div className="font-bold text-teal-300">Silo {s.silo_no}</div>
                                        <div className="text-[10px] text-slate-500">{fmt(s.started_at)} → {s.ended_at ? fmt(s.ended_at) : "ongoing"}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-black text-slate-100">{NUM(s.tons_moved_conveyor)} t</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {modal === "arrival" && (
                <TruckArrivalModal exportOp={exp} onClose={() => setModal(null)} onSuccess={() => { setModal(null); load(); }} />
            )}
            {modal === "weigh" && weighVisit && (
                <WeighOutModal exportOp={exp} visit={weighVisit} onClose={() => { setModal(null); setWeighVisit(null); }} onSuccess={() => { setModal(null); setWeighVisit(null); load(); }} />
            )}
            {modal === "silo-tons" && (
                <LogSiloTonsModal exportOp={exp} defaultSiloNo={exp.current_silo_no} onClose={() => setModal(null)} onSuccess={() => { setModal(null); load(); }} />
            )}
            {modal === "pause" && (
                <PauseExportModal exportOp={exp} onClose={() => setModal(null)} onSuccess={() => { setModal(null); load(); }} />
            )}
            {modal === "change" && (
                <ChangeSiloModal exportOp={exp} onClose={() => setModal(null)} onSuccess={() => { setModal(null); load(); }} />
            )}
            {modal === "pick-out" && (
                <TruckPickerModal
                    title="Truck Out — finish loading"
                    subtitle="Loading at station"
                    mode="at_station"
                    visits={atStation}
                    now={now}
                    onClose={() => setModal(null)}
                    onPick={(v) => { setModal(null); sendToWeighbridge(v.id); }}
                />
            )}
            {modal === "pick-weigh" && (
                <TruckPickerModal
                    title="Weigh Out — capture tons"
                    subtitle="Pending weigh-out"
                    mode="pending_weigh_out"
                    visits={pendingWeighOut.length > 0 ? pendingWeighOut : atStation}
                    now={now}
                    onClose={() => setModal(null)}
                    onPick={(v) => { setWeighVisit(v); setModal("weigh"); }}
                />
            )}

            {/* Persistent floating action button (mobile) — quick Truck In */}
            {canOperate && exp.status === "active" && !isPaused && !modal && (
                <button
                    onClick={() => setModal("arrival")}
                    className="xl:hidden fixed bottom-4 right-4 z-40 w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white shadow-2xl ring-4 ring-emerald-500/40 flex flex-col items-center justify-center gap-0.5 active:scale-95 transition-transform"
                    data-testid="lo-truck-in-fab"
                    aria-label="Truck in — scan and log arrival"
                >
                    <Truck size={22} weight="fill" />
                    <span className="text-[8px] font-black uppercase tracking-wider leading-none">TRUCK IN</span>
                </button>
            )}
        </div>
    );
}

function InfoRow({ label, value, icon: Icon, testId, status }) {
    const dot = status === "active" ? "text-emerald-400" : status === "completed" ? "text-slate-400" : null;
    return (
        <div className="flex items-center justify-between py-1.5 border-b border-slate-800/60 last:border-none" data-testid={testId}>
            <div className="flex items-center gap-2 text-[11px] uppercase text-slate-400 tracking-wider">
                {Icon && <Icon size={13} className="text-slate-500" />}
                {label}
            </div>
            <div className={`text-sm font-bold ${dot || "text-slate-100"} truncate max-w-[55%] text-right`}>{status && <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${dot === "text-emerald-400" ? "bg-emerald-400 animate-pulse" : "bg-slate-400"}`} />}{value}</div>
        </div>
    );
}

function Stat({ label, value, testId }) {
    return (
        <div className="bg-slate-800/60 rounded p-2 text-center" data-testid={testId}>
            <div className="text-[10px] uppercase text-slate-400 tracking-wider">{label}</div>
            <div className="text-lg font-black text-slate-100">{value}</div>
        </div>
    );
}
